import { Button as ButtonAnt, Dropdown, Menu } from 'antd'
import { Permission } from 'common/constants'
import { getNs } from 'common/i18n-config'
import React, { PropsWithChildren } from 'react'

import { ifElse, someTrue } from '../../helpers'
import { StyledTooltipOverlay } from '../../shared'
import { Tooltip } from '../tooltip'
import { Button, ButtonPerStyle, MenuIcon, MenuStyle, Wrapper } from './styles'

const { t, useNsReady } = getNs(['common', 'components/organisms/assets/asset-list'])

export const TableDropdownMenu = ({
  data,
  permissions,
  onCopy,
  onDelete,
  onCorrection,
  isEnableCorrectButton,
  isHavePermissionToCorrect,
}: PropsWithChildren<Props>) => {
  useNsReady()

  const handleCopy = (item: any, event: any) => {
    event.stopPropagation()
    permissions?.isCreate && onCopy && onCopy(item)
  }

  const handleDelete = (item: any, event: any) => {
    event.stopPropagation()
    permissions?.isDelete && item.isDeletable && onDelete && onDelete(item)
  }

  const handleCorrection = (biid: string, event: any) => {
    event.stopPropagation()
    onCorrection && onCorrection(biid)
  }

  const adjusted = data?.adjusted
  const Actions = (
    <Menu className={MenuStyle} onClick={event => event.domEvent.stopPropagation()}>
      <Menu.Item key="copy">
        {!permissions?.isCreate ? (
          withPermissionTooltip(
            <ButtonAnt disabled style={ButtonPerStyle} className="btn-no-permission">
              {t('actions.copy')}
            </ButtonAnt>
          )
        ) : (
          <Button
            type="text"
            data-testid={`copy-action-${data.key as string}`}
            id={`copy-action-${data.key as string}`}
            className="text"
            onClick={event => handleCopy(data, event)}
          >
            {t('actions.copy')}
          </Button>
        )}
      </Menu.Item>
      <Menu.Item key="correction">
        {!isHavePermissionToCorrect ? (
          withPermissionTooltip(
            <ButtonAnt type="link" className="btn-no-permission" disabled>
              {t('actions.correction')}
            </ButtonAnt>
          )
        ) : (
          <Tooltip
            content={ifElse(adjusted, t('tooltip_message.cancel_accelerated_depreciation_to_execute'), '')}
            placement="bottomRight"
          >
            <Button
              type="link"
              className="text"
              disabled={someTrue(!isEnableCorrectButton, adjusted)}
              onClick={e => handleCorrection(data?.biid, e)}
            >
              {t('actions.correction')}
            </Button>
          </Tooltip>
        )}
      </Menu.Item>
      <Menu.Item key="delete" style={{ cursor: !data.isDeletable ? 'not-allowed' : 'pointer' }}>
        {!permissions?.isDelete ? (
          withPermissionTooltip(
            <ButtonAnt disabled style={ButtonPerStyle} className="btn-no-permission">
              {t('actions.remove')}
            </ButtonAnt>
          )
        ) : (
          <Tooltip
            content={ifElse(adjusted, t('tooltip_message.cancel_accelerated_depreciation_to_execute'), '')}
            placement="bottomRight"
          >
            <Button
              danger
              type="text"
              data-testid={`delete-action-${data.key as string}`}
              id={`delete-action-${data.key as string}`}
              disabled={someTrue(!data.isDeletable, adjusted)}
              className="text"
              onClick={event => handleDelete(data, event)}
            >
              {t('actions.remove')}
            </Button>
          </Tooltip>
        )}
      </Menu.Item>
    </Menu>
  )

  return (
    <Wrapper>
      <Dropdown overlayClassName="popup-overlay-actions" placement="bottomRight" overlay={Actions} trigger={['click']}>
        <Button
          className="action-button"
          id={`popup-action-${data.key as string}`}
          type="text"
          onClick={event => event.stopPropagation()}
        >
          <MenuIcon />
        </Button>
      </Dropdown>
    </Wrapper>
  )
}

export const withPermissionTooltip = (WrappedComponent: React.ReactNode) => {
  return (
    <Tooltip
      overlayClassName={StyledTooltipOverlay}
      placement="top"
      content={t('actions.no_permission')}
      className="ant-tooltip-custom"
      trigger="hover"
    >
      {WrappedComponent}
    </Tooltip>
  )
}

type Props = {
  data: any
  permissions?: Permission
  onCopy?: (data: any) => void
  onDelete?: (data: any) => void
  onCorrection?: (data: any) => void
  isEnableCorrectButton?: boolean
  isHavePermissionToCorrect?: boolean
}
